import {Button, Size, Type} from '@Components/button';
import type {ReactElement} from 'react';
import React from 'react';
import {removeDuplicateColorPalettes} from '@Libraries/auto-branding-email-maker';
import {noop} from '@Utils/general.util';
import {openBrandKitUpdateFromColorPaletteModal, PANEL_ID} from '@Modals/brand-kit-update-from-color-palette-modal';
import styles from './brand-kit-palette-editor.module.scss';
import {ColorPalette} from '@Components/color-palettes/color-palette/color-palette';
import type {ColorPaletteInfo} from '@Libraries/color-palettes.library';
import {isSameColorPalette} from '@Libraries/color-palettes.library';
import {addPaletteToRecents} from '@Libraries/color-palettes.library';
import {useAppDispatch, useAppSelector} from '@/hooks';
import {ColorPalettesContainer} from '@Components/color-palettes/color-palettes-container';
import {setColorPaletteBeingApplied} from '@/reducers/color-palettes.reducer';

export interface BrandKitPaletteEditorProps {
  brandKitPalette: ColorPaletteInfo;
  suggestedPalettes: ColorPaletteInfo[];
  buttonCtaText?: string;
  buttonType?: Type;
  onPaletteSelect: (colorPalette: ColorPaletteInfo) => void;
  onBrandKitUpdate: VoidFunction;
  colorPaletteAppliedEvent: string;
}

export function BrandKitPaletteEditor({
  brandKitPalette,
  suggestedPalettes = [],
  buttonCtaText = window.i18next.t('pmwjs_update_brand_kit'),
  buttonType = Type.PRIMARY,
  onPaletteSelect = noop,
  onBrandKitUpdate = noop,
  colorPaletteAppliedEvent,
}: BrandKitPaletteEditorProps): ReactElement<any> | null {
  const dispatch = useAppDispatch();

  const selectedColorPalette = useAppSelector((state) => {
    return state.colorPalettes.selectedColorPalette ?? null;
  });

  if (!selectedColorPalette) {
    return null;
  }

  const handlePaletteSelection = (colorPalette: ColorPaletteInfo): void => {
    void addPaletteToRecents(colorPalette);

    onPaletteSelect(colorPalette);
  };

  const getUpdateBrandKitButtonComponent = (): React.ReactElement<any> | null => {
    if (isSameColorPalette(selectedColorPalette, brandKitPalette)) {
      return null;
    }

    return (
      <div className={styles.background}>
        <Button
          type={buttonType}
          size={Size.SMALL}
          text={buttonCtaText}
          textClasses="_text-transform-none"
          onClick={() => {
            openBrandKitUpdateFromColorPaletteModal({
              panelId: PANEL_ID,
              currentColorPalette: brandKitPalette,
              newColorPalette: selectedColorPalette,
              onBrandKitUpdate,
            });
          }}
        />
      </div>
    );
  };

  const onColorPaletteDoneApplying = (): void => {
    dispatch(setColorPaletteBeingApplied(undefined));
  };

  return (
    <>
      <div className="spacing-m-t-3 spacing-m-b-6">
        <ColorPalette colorPaletteAppliedEvent={colorPaletteAppliedEvent} palette={selectedColorPalette} isSelected />
      </div>
      {getUpdateBrandKitButtonComponent()}
      <ColorPalettesContainer
        onColorPaletteDoneApplying={onColorPaletteDoneApplying}
        useUniqueStringAsKeyForPalette
        colorPaletteAppliedEvent={colorPaletteAppliedEvent}
        heading={window.i18next.t('pmwjs_more_suggestions')}
        colorPalettes={removeDuplicateColorPalettes(suggestedPalettes)}
        onColorPaletteSelected={handlePaletteSelection}
        showPaletteName={false}
        showNameOverlayOnHover={false}
      />
    </>
  );
}
